import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getTerminology, TerminologyContext } from "@/lib/terminology";
import { toast } from "sonner";
import {
  Scissors, CalendarRange, Wrench, Users, Clock,
  Ban, Bell, LogOut, Menu, X, Building2, KeyRound,
} from "lucide-react";
import AgendaTab from "@/components/admin/AgendaTab";
import ServicesTab from "@/components/admin/ServicesTab";
import BarbersTab from "@/components/admin/BarbersTab";
import HoursTab from "@/components/admin/HoursTab";
import BlocksTab from "@/components/admin/BlocksTab";
import NotificationsTab from "@/components/admin/NotificationsTab";
import TenantProfileTab from "@/components/admin/TenantProfileTab";
import AccountSecurityTab from "@/components/admin/AccountSecurityTab";

const NAV = [
  { id: "agenda", label: "Agenda", icon: CalendarRange },
  { id: "servicos", label: "Serviços", icon: Wrench },
  { id: "barbeiros", label: "Profissionais", icon: Users },
  { id: "horarios", label: "Horários", icon: Clock },
  { id: "bloqueios", label: "Bloqueios & Folgas", icon: Ban },
  { id: "notificacoes", label: "Notificações", icon: Bell },
  { id: "perfil", label: "Perfil", icon: Building2 },
  { id: "conta", label: "Minha conta", icon: KeyRound },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).has("payment_connection") ? "perfil" : "agenda");
  const [dashboard, setDashboard] = useState(null);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [accessUnavailable, setAccessUnavailable] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const loadCommon = async () => {
    try {
      const [d, b, s, currentTenant] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/barbers"),
        api.get("/admin/services"),
        api.get("/admin/tenant"),
      ]);
      setDashboard(d.data);
      setBarbers(b.data);
      setServices(s.data);
      setTenant(currentTenant.data);
    } catch (error) {
      if (error?.response?.status === 403) setAccessUnavailable(true);
      else {
      toast.error("Erro ao carregar dados.");
      }
    }
  };

  useEffect(() => { loadCommon(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const NavItems = ({ onClick }) => (
    <>
      {NAV.map((n) => {
        const Icon = n.icon;
        return (
          <button
            key={n.id}
            data-testid={`nav-tab-${n.id}`}
            onClick={() => { setTab(n.id); onClick?.(); }}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-sans2 transition-colors ${
              tab === n.id
                ? "bg-[#D4AF37]/12 text-[#D4AF37] border border-[#D4AF37]/30"
                : "text-stone-400 hover:text-amber-100 hover:bg-[#1A1D26] border border-transparent"
            }`}
          >
            <Icon className="w-4 h-4" /> {n.label}
          </button>
        );
      })}
    </>
  );

  if (accessUnavailable) return <div className="min-h-screen flex items-center justify-center p-6 text-center"><div className="card-surface max-w-md p-8"><h1 className="font-display text-2xl gold-text">Acesso indisponível</h1><p className="mt-3 text-stone-300">O acesso deste estabelecimento está temporariamente indisponível. Entre em contato com o administrador da plataforma.</p><button onClick={handleLogout} className="mt-6 rounded bg-[#D4AF37] px-4 py-2 font-semibold text-[#0A0B0E]">Sair</button></div></div>;

  return (
    <TerminologyContext.Provider value={getTerminology(tenant?.business_type)}>
    <div className="min-h-screen flex" data-theme={tenant?.theme_id || "PREMIUM_GOLD"}>
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-80 flex-col border-r border-[#282C37] bg-[#0C0D12] p-4 fixed h-full">
        <div data-testid="tenant-brand-name" className="px-2 mb-8 min-h-16 flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/10"><Scissors className="w-5 h-5 text-[#D4AF37]" /></div>
          <span className="font-display text-lg leading-tight gold-text line-clamp-2 break-words min-w-0">{tenant?.name || "Estabelecimento"}</span>
        </div>
        <nav className="space-y-1.5 flex-1"><NavItems /></nav>
        <div className="border-t border-[#282C37] pt-4">
          <div className="flex items-center gap-2 px-2 mb-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="min-w-0">
              <p className="text-xs text-amber-100 truncate font-sans2">{user?.name}</p>
              <p className="text-[10px] text-stone-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-stone-400 hover:text-red-400 hover:bg-red-500/10 transition-colors font-sans2"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0A0B0E]/90 backdrop-blur-xl border-b border-[#282C37] h-14 flex items-center justify-between px-4">
        <div data-testid="tenant-mobile-brand-name" className="flex items-center gap-2 min-w-0 pr-3"><Scissors className="w-5 h-5 shrink-0 text-[#D4AF37]" /><span className="font-display gold-text text-sm leading-tight line-clamp-2 break-words min-w-0">{tenant?.name || "Estabelecimento"}</span></div>
        <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)}>
          <Menu className="w-6 h-6 text-amber-100" />
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-72 bg-[#0C0D12] border-l border-[#282C37] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <span className="font-display gold-text">Menu</span>
              <button onClick={() => setMenuOpen(false)}><X className="w-6 h-6 text-stone-400" /></button>
            </div>
            <nav className="space-y-1.5"><NavItems onClick={() => setMenuOpen(false)} /></nav>
            <button
              onClick={handleLogout}
              className="w-full mt-6 flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 font-sans2"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-80 pt-20 lg:pt-8 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-6xl mx-auto">
          {tab === "agenda" && (
            <AgendaTab dashboard={dashboard} barbers={barbers} reloadDashboard={loadCommon} />
          )}
          {tab === "servicos" && <ServicesTab services={services} reload={loadCommon} />}
          {tab === "barbeiros" && <BarbersTab barbers={barbers} services={services} reload={loadCommon} />}
          {tab === "horarios" && <HoursTab barbers={barbers} />}
          {tab === "bloqueios" && <BlocksTab barbers={barbers} />}
          {tab === "notificacoes" && <NotificationsTab />}
          {tab === "perfil" && <TenantProfileTab tenant={tenant} reload={loadCommon} />}
          {tab === "conta" && <AccountSecurityTab />}
        </div>
      </main>
    </div>
    </TerminologyContext.Provider>
  );
}
